import React, { forwardRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { Theme } from '@mui/material';
import { AppDispatch } from '@typings/redux';
import { filtersThemeFactory } from 'styles/themes/filtersTheme';
import { Form, SelectBox, DatePicker } from 'components/shared/form';
import { SearchInput } from 'components/shared/form/SearchInput/SearchInput';
import { useStyles } from 'components/institution/Browser/Toolbar/Toolbar.styles';
import { useTranslator } from 'components/shared/hooks/useTranslator/useTranslator';
import { AutoSave } from 'components/shared/form/AutoSave/AutoSave';
import { DataChangeFiltersModel, Origin } from '../AccessList.types';
import { dataChangeRegisterListSelector } from 'store/reports/reports.selector';
import { setDataChangeRegisterListFilters } from 'store/reports/reports.action';
import { FocusTrap } from 'components/shared/form/FocusTrap/FocusTrap';

export interface ToolbarProps {
  onGridFocus: () => void;
}

const ToolbarComponent = (props: ToolbarProps, ref: React.ForwardedRef<HTMLElement>) => {
  const classes = useStyles();
  const trans = useTranslator();
  const dispatch = useDispatch() as AppDispatch;
  const { filters } = useSelector(dataChangeRegisterListSelector);

  const valuesChanged = (values: DataChangeFiltersModel) => {
    dispatch(setDataChangeRegisterListFilters(values));
  };

  return (
    <section ref={ref} className={classes.toolbar}>
      <Form<DataChangeFiltersModel>
        initialValues={filters}
        enableReinitialize={true}
        onSubmit={valuesChanged}
        className={classes.form}
      >
        {() => (
          <div className={classes.filters}>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={(parentTheme: Theme) => filtersThemeFactory(parentTheme)}>
                <DatePicker
                  name="dateFrom"
                  labelText={trans('REPORTS.SYNCHRONIZATIONS.FILTERS.DATE_FROM.LABEL')}
                  required
                  clearable={false}
                  disableKeyboard
                />
                <DatePicker
                  name="dateTo"
                  labelText={trans('REPORTS.SYNCHRONIZATIONS.FILTERS.DATE_TO.LABEL')}
                  required
                  clearable={false}
                  disableKeyboard
                />
                <SearchInput
                  name="userLogin"
                  labelText={trans(
                    'PERSONAL_DATA_ACCESS.USER_PERSONAL_DATA_CHANGE_REGISTER.FILTER_TOOLBAR.USER_LOGIN.LABEL',
                  )}
                  resettable
                  tooltipText={trans(
                    'PERSONAL_DATA_ACCESS.USER_PERSONAL_DATA_CHANGE_REGISTER.FILTER_TOOLBAR.LAST_NAME.SEARCH',
                  )}
                />
                <SelectBox
                  name="origin"
                  placeholderText={trans(
                    'PERSONAL_DATA_ACCESS.USER_PERSONAL_DATA_CHANGE_REGISTER.FILTER_TOOLBAR.ORIGIN.ALL',
                  )}
                  labelText={trans(
                    'PERSONAL_DATA_ACCESS.USER_PERSONAL_DATA_CHANGE_REGISTER.FILTER_TOOLBAR.STUDENT_ORIGIN.LABEL',
                  )}
                  displayEmpty
                  options={[
                    {
                      value: Origin.CHILD_REGISTER,
                      label: trans('PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.COLUMNS.ORIGIN.CHILD_REGISTER'),
                    },
                    {
                      value: Origin.STUDENTS,
                      label: trans('PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.COLUMNS.ORIGIN.STUDENTS'),
                    },
                  ]}
                />
                <SearchInput
                  name="identifierValue"
                  labelText={trans(
                    'PERSONAL_DATA_ACCESS.USER_PERSONAL_DATA_CHANGE_REGISTER.FILTER_TOOLBAR.IDENTIFIER_VALUE.LABEL',
                  )}
                  resettable
                  tooltipText={trans(
                    'PERSONAL_DATA_ACCESS.USER_PERSONAL_DATA_CHANGE_REGISTER.FILTER_TOOLBAR.IDENTIFIER_VALUE.SEARCH',
                  )}
                />
                <SearchInput
                  name="nameAndLastName"
                  labelText={trans(
                    'PERSONAL_DATA_ACCESS.USER_PERSONAL_DATA_CHANGE_REGISTER.FILTER_TOOLBAR.CHILD_NAME.LABEL',
                  )}
                  resettable
                  tooltipText={trans(
                    'PERSONAL_DATA_ACCESS.USER_PERSONAL_DATA_CHANGE_REGISTER.FILTER_TOOLBAR.CHILD_NAME.SEARCH',
                  )}
                />
                <AutoSave debounced={['identifierValue', 'userLogin', 'nameAndLastName']} />
                <FocusTrap handler={() => props.onGridFocus()} />
              </ThemeProvider>
            </StyledEngineProvider>
          </div>
        )}
      </Form>
    </section>
  );
};

ToolbarComponent.displayName = 'Toolbar';
export const Toolbar = forwardRef(ToolbarComponent);
